import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';

const devUrl = environment.backendurl;

/**
 * Data structure of each request object for talent insights export
 */
export interface ExportRequest {
  type: string
  action?: string
  data: { 
    currentTab: string 
  }
}

/**
 * Data structure of each service payload object for talent insights export
 */
export interface ExportPayload {
  type?: string
  tab: string
  totalresultcount?: number
  data?: any
  aiData?: string
  aiRequestFilters?: any
  aiRequestPayload?: any[]
  [key: string]: any
}

@Injectable({
  providedIn: 'root'
})

export class CompanyInsightsService {
  public companyListData: any = null;
  private companyData : any = null;

  /**
   * Observable for handling insights-export request signal
   */
  private insightsExportRequestObservable: Subject<any> = new Subject();
  /**
   * Observable for handling insights-export data sharing
   */
  private insightsExportDataObservable: Subject<any> = new Subject();

  constructor(private http: HttpClient) { }

  /**
   * Getter for insights-export request observable
   */
  get insightsExportRequestEvent(): Observable<any> {
    return this.insightsExportRequestObservable.asObservable();
  }

  /**
   * Getter for insights-export request observable
   */
  get insightsExportDataEvent(): Observable<any> {
    return this.insightsExportDataObservable.asObservable();
  }

  /**
   * Send request for insights export
   * @param requestData 
   */
  sendInsightsExportRequest(requestData: ExportRequest) {
    this.insightsExportRequestObservable.next(requestData);
  }

  /**
   * Emit data for insights export
   * @param exportData 
   */
  sendInsightsExportData(exportData: ExportPayload) {
    this.insightsExportDataObservable.next(exportData);
  }

  getRecommendedcompanies(payload: any){
    var url = devUrl + `/company/recommendcompanies`;
    return this.http.post(url, payload).pipe(
      map((response) => {
        return response;
      }),
      catchError(GlobalService.handleError("get recommended companies", url))
    );
  }

  getIndustryAggregates(payload:any) {
    var url = devUrl + `/company/aggregates`;
    return this.http.post(url, payload).pipe(
      map((response) => {
        return response;
      }),
      catchError(GlobalService.handleError("get aggregates", url))
    );
  }

  getCompanyTypeaheads(keyword: string) {
    var url = devUrl + `/company/typeaheads?keyword=${keyword}`;
    return this.http.get(url).pipe(
      map((response) => {
        return response;
      }),
      catchError(GlobalService.handleError("get aggregates", url))
    );
  }

  setCompanyListData(data: any): void {
    this.companyListData = data;
    setTimeout(() => this.companyListData = [], 2000);
  }
  getCompanyListData(): any {
    return this.companyListData;
  }

  setCompanyData(data: any) {
    this.companyData = Object.assign({}, data);
  }
  get fetchCompanyData() {
    return this.companyData;
  }
  
  getCompanyDetailsById(companyId: any) {
    const queryParams = { id: companyId };
    const url = devUrl + `/company/detailsbyid`;
    return this.http.get(url, { params: queryParams }).pipe(
      map((response) => {
        return response;
      }),
      catchError(GlobalService.handleError("get company details", url))
    );
  }

  getTotalCompanyaggs(requestobject):  Observable<any> {
    requestobject['calledvia'] = "companypages";
    requestobject['companytype'] = "Current";
    requestobject['jobtitletype'] = "Current";
    requestobject['industrytype'] = "Current";
    requestobject['functiontype'] = "Current";

    const url = devUrl + '/personsearch/aggregates';
    return this.http.post<any>(url, requestobject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getchangedjobscount(requestObject): Observable<any> {
    const url = devUrl + '/personsearch/changedjobscount';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getcompanywinningTalentFrom(requestObject): Observable<any> {
    const url = devUrl + '/personsearch/companywinningtalentfrom';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getcompanylosingtalentto(requestObject): Observable<any> {
    const url = devUrl + '/personsearch/companylosingtalentto';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getcompanyinsightssummary(requestObject): Observable<any> {
    const url = devUrl + '/openai/companyinsightssummary';
    return this.http.post(url, requestObject).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  /**
   * Export insights report to PDF
   * @param companyId 
   * @param payload 
   */
  exportInsightsToPDFReport(companyId: string, payload: any): Observable<any> {
    const url = devUrl + `/personsearch/company/${companyId}/insights/export`;
    return this.http.post(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Company Insights Export to PDF', url)));
  }

}
